/**
 * Deferred video elements.
 *
 * <lc-video>      plays when it nears the viewport
 * <lc-card-video> plays on deliberate hover, desktop only
 *
 * Both ship with no `src` and preload="none", so nothing downloads until the
 * element decides to load. Autonomous custom elements, not `is=` customized
 * built-ins: Safari has declined to implement the latter, so `<video is="...">`
 * would never upgrade on iOS.
 */

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Touch devices cannot hover, so they must never pay for a hover video. */
const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

class LcVideoBase extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.src = this.dataset.src;
    if (!this.video || !this.src) return;
    this.setup();
  }

  /**
   * Attaching the source lazily is the whole point of these elements; doing it
   * twice would restart playback, so guard on `loaded`.
   */
  load() {
    if (this.loaded) return;
    this.loaded = true;
    this.video.src = this.src;
    this.video.load();
  }

  play() {
    this.load();
    this.video
      .play()
      .then(() => this.setAttribute('data-playing', ''))
      // Browsers reject play() for reasons outside our control (Low Power Mode,
      // autoplay policy). Leaving the poster painted is the correct fallback.
      .catch(() => {});
  }

  pause() {
    if (!this.loaded) return;
    this.video.pause();
    this.removeAttribute('data-playing');
  }
}

class LcVideo extends LcVideoBase {
  setup() {
    if (!this.hasAttribute('data-autoplay')) return;
    if (prefersReducedMotion()) return; // leave the poster painted

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.play();
          else this.pause();
        }
      },
      // Start fetching just before it scrolls into view so the swap isn't visible.
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

class LcCardVideo extends LcVideoBase {
  setup() {
    if (!canHover() || prefersReducedMotion()) return;

    // The element itself is pointer-events:none so it never swallows clicks on
    // the card's link, which also means it receives no pointer events. Listen on
    // the whole card, so the video starts on entering the card rather than only
    // the overlay that happens to contain it.
    this.trigger = this.closest('lc-product-card') ?? this.parentElement;
    if (!this.trigger) return;

    this.trigger.addEventListener('pointerenter', this.onEnter);
    this.trigger.addEventListener('pointerleave', this.onLeave);
  }

  disconnectedCallback() {
    clearTimeout(this.dwell);
    this.trigger?.removeEventListener('pointerenter', this.onEnter);
    this.trigger?.removeEventListener('pointerleave', this.onLeave);
  }

  // A dwell delay keeps a mouse crossing the grid from firing every video in its path.
  onEnter = () => {
    this.dwell = setTimeout(() => this.play(), 100);
  };

  onLeave = () => {
    clearTimeout(this.dwell);
    this.pause();
  };
}

customElements.define('lc-video', LcVideo);
customElements.define('lc-card-video', LcCardVideo);
